import React from "react";
import { useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { FiArrowRight, FiClock, FiGift, FiShield, FiTrendingUp, FiUsers } from "react-icons/fi";

const HERO_IMAGE = "https://storageaccountzampl852f.blob.core.windows.net/zamplia-survey-images/heroGroup.avif";

const highlights = [
  { label: "Instant Rewards", icon: FiGift },
  { label: "Quick Surveys", icon: FiClock },
  { label: "Privacy Protected", icon: FiShield },
];

/* Faint measurement grid behind the hero, faded out toward the edges. */
const gridStyle: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(to right, rgb(var(--zs-line) / 0.55) 1px, transparent 1px), linear-gradient(to bottom, rgb(var(--zs-line) / 0.55) 1px, transparent 1px)",
  backgroundSize: "44px 44px",
  WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 30%, transparent 75%)",
  maskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 30%, transparent 75%)",
};

export const HeroSession = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    location.pathname === "/join-now" ? window.scrollTo(0, 0) : navigate("/join-now");
  };

  return (
    <section className="relative overflow-hidden border-b bg-canvas border-line">
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={gridStyle} />

      <div className="relative grid items-center gap-12 px-4 py-16 mx-auto max-w-7xl sm:px-6 sm:py-20 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:py-24">
        {/* Copy */}
        <div className="lg:col-span-6 xl:col-span-6">
          <span className="zs-badge zs-badge-primary zs-badge-dot">Market research panel</span>

          <h1 className="mt-5 hero-title text-ink">
            Share Your Opinion
            <br />
            <span className="text-primary-text">Get Rewarded</span> Instantly!
          </h1>

          <p className="max-w-xl mt-6 text-base leading-relaxed sm:text-lg text-ink-2">
            Join thousands earning rewards for quick, easy surveys.
          </p>

          <div className="flex flex-col gap-3 mt-8 sm:flex-row sm:items-center">
            <button onClick={handleGetStarted} className="zs-btn zs-btn-primary zs-btn-lg">
              Get Started Now
              <FiArrowRight aria-hidden="true" />
            </button>
            <HashLink smooth to="/#feature" className="zs-btn zs-btn-secondary zs-btn-lg">
              Explore features
            </HashLink>
          </div>

          <ul className="grid gap-3 mt-10 sm:grid-cols-3 sm:gap-4">
            {highlights.map(({ label, icon: Icon }) => (
              <li key={label} className="flex items-center gap-3 text-sm font-medium text-ink">
                <span className="zs-stat-icon">
                  <Icon aria-hidden="true" />
                </span>
                {label}
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3 pt-6 mt-8 text-sm border-t border-line text-ink-3">
            <FiUsers aria-hidden="true" className="text-base text-primary-text" />
            <span className="font-semibold text-ink">Trusted by 1M+ users</span>
          </div>
        </div>

        {/* Product imagery */}
        <div className="relative lg:col-span-6 xl:col-span-6">
          <div className="relative overflow-hidden border rounded-xl border-line bg-surface shadow-md">
            <div className="flex items-center gap-1.5 px-4 h-10 border-b border-line bg-surface-2" aria-hidden="true">
              <span className="w-2.5 h-2.5 rounded-full bg-line-strong" />
              <span className="w-2.5 h-2.5 rounded-full bg-line-strong" />
              <span className="w-2.5 h-2.5 rounded-full bg-line-strong" />
            </div>
            <div className="flex items-end justify-center px-6 pt-8 bg-primary-soft/60 dark:bg-primary-soft/40">
              <img src={HERO_IMAGE} alt="friends" className="w-full max-w-md animate-slideInUp" />
            </div>
          </div>

          {/* Floating summary cards */}
          <div className="absolute hidden w-60 p-4 zs-card shadow-lg sm:block -left-4 top-20 lg:-left-8 animate-slideInLeft">
            <div className="flex items-center gap-3">
              <span className="zs-stat-icon">
                <FiUsers aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">You&apos;re in good community</p>
                <p className="mt-0.5 text-xs text-ink-3">1M+ people already earning.</p>
              </div>
            </div>
          </div>

          <div className="absolute hidden p-4 zs-card shadow-lg sm:block w-60 -right-4 top-1/2 lg:-right-6 animate-slideInRight">
            <div className="flex items-center gap-3">
              <span className="zs-stat-icon">
                <FiGift aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">Take a quick break</p>
                <p className="mt-0.5 text-xs text-ink-3">Do a survey, get a reward.</p>
              </div>
            </div>
          </div>

          <div className="absolute hidden p-4 zs-card shadow-lg sm:block w-56 left-8 -bottom-6 animate-slideInUp">
            <div className="flex items-center justify-between gap-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                <FiTrendingUp aria-hidden="true" className="text-success" />
                {`Today's Earning`}
              </p>
              <p className="text-xs font-semibold tabular-nums text-ink-2">60%</p>
            </div>
            <div className="h-2 mt-3 overflow-hidden rounded-full bg-surface-hover">
              <div className="h-full rounded-full bg-success w-[60%]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
